import { SupportProfile, DeliveryMode, CustomerType } from '../../types/supportProfile';
import {
  PricingProfile,
  LabourRates,
  PricingProfileConfig,
  RateSlot,
  ResolvedRateSlot,
  ResolvedPricingProfile,
} from '../../types/pricingProfile';
import { CatalogProduct } from '../../contracts/product';
import { resolveSupportSku } from '../dataverse/customerContextMapper';

const DELIVERY_MODES: DeliveryMode[] = ['Remote', 'Onsite', 'Instore', 'Automation'];

const DELIVERY_MODE_TO_RATE_KEY: Record<DeliveryMode, keyof LabourRates> = {
  Remote: 'remoteRate',
  Onsite: 'onsiteRate',
  Instore: 'instoreRate',
  Automation: 'automationRate',
};

/**
 * Derives a PricingProfile with labourOverrides from the product catalog.
 *
 * For each delivery mode, resolves the support SKU using the customer's SupportProfile,
 * looks it up in the catalog, and maps the unitCost to the corresponding rate field.
 * Also resolves after-hours rates (SKUs with .AH suffix).
 *
 * Missing SKUs are skipped (the rate stays undefined). Returns {} if no rates resolve.
 */
export function derivePricingProfileFromCatalog(
  supportProfile: SupportProfile,
  catalog: CatalogProduct[]
): PricingProfile {
  const skuMap = new Map<string, number>();
  for (const product of catalog) {
    if (!product.disabled) {
      skuMap.set(product.sku, product.unitCost);
    }
  }

  const standardRates = resolveRatesForModes(supportProfile, skuMap, false);
  const afterHoursRates = resolveRatesForModes(supportProfile, skuMap, true);

  const hasStandardRates = hasAnyRate(standardRates);
  const hasAfterHoursRates = hasAnyRate(afterHoursRates);

  if (!hasStandardRates && !hasAfterHoursRates) {
    return {};
  }

  return {
    labourOverrides: {
      ...standardRates,
      ...(hasAfterHoursRates ? { afterHours: afterHoursRates } : {}),
    },
  };
}

function resolveRatesForModes(
  supportProfile: SupportProfile,
  skuMap: Map<string, number>,
  afterHours: boolean
): LabourRates {
  const rates: LabourRates = {};

  for (const mode of DELIVERY_MODES) {
    const sku = resolveSupportSku(supportProfile, {
      deliveryMode: mode,
      afterHours,
    });

    const unitCost = skuMap.get(sku);
    if (unitCost !== undefined) {
      rates[DELIVERY_MODE_TO_RATE_KEY[mode]] = unitCost;
    }
  }

  return rates;
}

function hasAnyRate(rates: LabourRates): boolean {
  return Object.values(rates).some(v => v !== undefined);
}

// ---------------------------------------------------------------------------
// Config-driven pricing profile resolution (pricing-profiles.json)
// ---------------------------------------------------------------------------

/**
 * Deep-merges a base RateSlot with a designation override.
 * Designation fields win; defaultQuantity is merged per-key.
 */
function mergeRateSlots(base: RateSlot, override: RateSlot): RateSlot {
  return {
    sku: override.sku ?? base.sku,
    afterHoursSku: override.afterHoursSku ?? base.afterHoursSku,
    defaultQuantity: {
      ...base.defaultQuantity,
      ...override.defaultQuantity,
    },
  };
}

/**
 * Resolves a full pricing profile by cascading config defaults + designation
 * overrides, then looking up SKU prices in the product catalog.
 *
 * Cascade logic:
 *   1. Start with `config.defaults[customerType]` as the base rates.
 *   2. If a designation exists in `config.designations`, deep-merge each
 *      delivery mode (designation fields win, defaultQuantity merged per-key).
 *   3. For each merged rate slot, look up `sku` and `afterHoursSku` in the
 *      catalog to populate `unitCost` / `afterHoursUnitCost`.
 *
 * Delivery modes whose SKU is missing from the catalog are omitted.
 */
export function resolvePricingProfile(
  config: PricingProfileConfig,
  customerType: CustomerType,
  designation: string | undefined,
  catalog: CatalogProduct[]
): ResolvedPricingProfile {
  const skuMap = new Map<string, number>();
  for (const product of catalog) {
    if (!product.disabled) {
      skuMap.set(product.sku, product.unitCost);
    }
  }

  const baseProfile = config.defaults[customerType];
  const baseRates: Record<string, RateSlot> = baseProfile?.rates ?? {};

  const designationProfile = designation ? config.designations[designation] : undefined;
  const designationRates: Record<string, RateSlot> = designationProfile?.rates ?? {};

  // Collect all delivery mode keys from both base and designation
  const allModes = new Set([...Object.keys(baseRates), ...Object.keys(designationRates)]);

  const resolved: ResolvedPricingProfile = {};

  for (const mode of allModes) {
    const base = baseRates[mode] ?? {};
    const override = designationRates[mode] ?? {};
    const merged = mergeRateSlots(base, override);

    if (!merged.sku) continue;

    const unitCost = skuMap.get(merged.sku);
    if (unitCost === undefined) continue;

    const slot: ResolvedRateSlot = {
      sku: merged.sku,
      unitCost,
      defaultQuantity: merged.defaultQuantity ?? {},
    };

    if (merged.afterHoursSku) {
      const ahCost = skuMap.get(merged.afterHoursSku);
      if (ahCost !== undefined) {
        slot.afterHoursSku = merged.afterHoursSku;
        slot.afterHoursUnitCost = ahCost;
      }
    }

    resolved[mode] = slot;
  }

  return resolved;
}
